import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Team, User, TeamMember } from '../../lib/types';

interface TeamWithMemberCount extends Team {
  memberCount?: number;
}

interface TeamMemberDetails extends TeamMember {
  name?: string;
  email?: string;
  role?: string;
}

interface Message {
  type: 'success' | 'error';
  text: string;
}

type TabType = 'teams' | 'members' | 'capacity';

const API_BASE = '';

// API Functions
async function getTeams(): Promise<Team[]> {
  const response = await fetch(`${API_BASE}/teams`);
  const data = await response.json();
  return data.data || [];
}

async function createTeam(teamData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/teams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(teamData),
  });
  return response.json();
}

async function updateTeam(teamId: string, teamData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/teams/${teamId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(teamData),
  });
  return response.json();
}

async function deleteTeam(teamId: string): Promise<any> {
  const response = await fetch(`${API_BASE}/teams/${teamId}`, {
    method: 'DELETE',
  });
  return response.json();
}

async function getUsers(): Promise<User[]> {
  const response = await fetch(`${API_BASE}/users`);
  const data = await response.json();
  return data.data || [];
}

async function getTeamMembers(teamId?: string): Promise<TeamMemberDetails[]> {
  const url = teamId ? `${API_BASE}/team-members?teamId=${teamId}` : `${API_BASE}/team-members`;
  const response = await fetch(url);
  const data = await response.json();
  return data.data || [];
}

async function createTeamMember(memberData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/team-members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(memberData),
  });
  return response.json();
}

async function updateTeamMember(memberId: string, memberData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/team-members/${memberId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(memberData),
  });
  return response.json();
}

async function removeTeamMember(memberId: string): Promise<any> {
  const response = await fetch(`${API_BASE}/team-members/${memberId}`, {
    method: 'DELETE',
  });
  return response.json();
}

export default function TeamManagement() {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('teams');
  const [message, setMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState(false);

  // Teams Tab State
  const [teams, setTeams] = useState<TeamWithMemberCount[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamFormData, setTeamFormData] = useState({
    teamName: '',
    department: '',
    teamLeadEmail: '',
  });

  // Members Tab State
  const [teamMembers, setTeamMembers] = useState<TeamMemberDetails[]>([]);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('');
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberDetails | null>(null);
  const [memberFormData, setMemberFormData] = useState({
    teamId: '',
    userId: '',
    role: '',
  });

  // Capacity Tab State
  const [capacityData, setCapacityData] = useState<any[]>([]);
  const [capacityFilter, setCapacityFilter] = useState<'all' | 'team' | 'department'>('all');
  const [selectedCapacityTeam, setSelectedCapacityTeam] = useState<string>('');

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'teams') {
      loadTeamsData();
    } else if (activeTab === 'members') {
      loadMembersData();
    } else if (activeTab === 'capacity') {
      loadCapacityData();
    }
  }, [activeTab]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadTeamsData = async () => {
    try {
      setLoading(true);
      const [teamsData, usersData] = await Promise.all([getTeams(), getUsers()]);
      setTeams(teamsData);
      setUsers(usersData);
    } catch (err) {
      console.error('Failed to load teams:', err);
      showMessage('error', 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadMembersData = async () => {
    try {
      setLoading(true);
      const [membersData, usersData, teamsData] = await Promise.all([
        getTeamMembers(selectedTeamFilter || undefined),
        getUsers(),
        getTeams(),
      ]);
      setTeamMembers(membersData);
      setUsers(usersData);
      setTeams(teamsData);
    } catch (err) {
      console.error('Failed to load members:', err);
      showMessage('error', 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadCapacityData = async () => {
    try {
      setLoading(true);
      const teamsData = await getTeams();
      const membersData = await getTeamMembers();

      // Calculate capacity for each team
      const capacityByTeam = teamsData.map(team => {
        const teamMembers = membersData.filter(m => m.TeamId === team.TeamId);
        const totalCapacity = teamMembers.reduce((sum, m) => sum + ((m.ProjectAllocationPercent || 0) + (m.WeeklyHours || 0)), 0);
        const allocatedPercent = Math.min(100, (totalCapacity / (teamMembers.length * 100)) * 100) || 0;

        return {
          teamId: team.TeamId,
          teamName: team.TeamName,
          department: team.Department,
          memberCount: teamMembers.length,
          totalCapacity: 100 * teamMembers.length,
          allocatedCapacity: totalCapacity,
          allocatedPercent: allocatedPercent,
          availablePercent: Math.max(0, 100 - allocatedPercent),
          isOverAllocated: allocatedPercent > 100,
        };
      });

      setCapacityData(capacityByTeam);
      setTeams(teamsData);
    } catch (err) {
      console.error('Failed to load capacity data:', err);
      showMessage('error', 'Failed to load capacity data');
    } finally {
      setLoading(false);
    }
  };

  // Teams Tab Handlers
  const handleAddTeam = () => {
    setEditingTeam(null);
    setTeamFormData({ teamName: '', department: '', teamLeadEmail: '' });
    setShowTeamForm(true);
  };

  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    setTeamFormData({
      teamName: team.TeamName,
      department: team.Department,
      teamLeadEmail: team.TeamLeadEmail,
    });
    setShowTeamForm(true);
  };

  const handleSubmitTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamFormData.teamName || !teamFormData.department) {
      showMessage('error', 'Please fill all required fields');
      return;
    }

    try {
      const result = editingTeam
        ? await updateTeam(editingTeam.TeamId, teamFormData)
        : await createTeam(teamFormData);

      if (result.success) {
        showMessage('success', editingTeam ? 'Team updated successfully' : 'Team created successfully');
        setShowTeamForm(false);
        await loadTeamsData();
      } else {
        showMessage('error', result.error || 'Failed to save team');
      }
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to save team');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!confirm('Are you sure you want to delete this team?')) return;

    try {
      const result = await deleteTeam(teamId);
      if (result.success) {
        showMessage('success', 'Team deleted successfully');
        await loadTeamsData();
      } else {
        showMessage('error', result.error || 'Failed to delete team');
      }
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to delete team');
    }
  };

  // Members Tab Handlers
  const handleAddMember = () => {
    setEditingMember(null);
    setMemberFormData({ teamId: selectedTeamFilter, userId: '', role: '' });
    setShowMemberForm(true);
  };

  const handleEditMember = (member: TeamMemberDetails) => {
    setEditingMember(member);
    setMemberFormData({
      teamId: member.TeamId,
      userId: member.UserId,
      role: member.role || '',
    });
    setShowMemberForm(true);
  };

  const handleSubmitMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberFormData.teamId || !memberFormData.userId) {
      showMessage('error', 'Please fill all required fields');
      return;
    }

    try {
      const result = editingMember
        ? await updateTeamMember(editingMember.TeamMemberId, memberFormData)
        : await createTeamMember(memberFormData);

      if (result.success) {
        showMessage('success', editingMember ? 'Member updated successfully' : 'Member added successfully');
        setShowMemberForm(false);
        await loadMembersData();
      } else {
        showMessage('error', result.error || 'Failed to save member');
      }
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to save member');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this member from the team?')) return;

    try {
      const result = await removeTeamMember(memberId);
      if (result.success) {
        showMessage('success', 'Member removed successfully');
        await loadMembersData();
      } else {
        showMessage('error', result.error || 'Failed to remove member');
      }
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to remove member');
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const getTeamName = (teamId: string) => {
    return teams.find(t => t.TeamId === teamId)?.TeamName || teamId;
  };

  const getUserName = (userId: string) => {
    return users.find(u => u.UserId === userId)?.Name || userId;
  };

  const getUserEmail = (userId: string) => {
    return users.find(u => u.UserId === userId)?.Email || userId;
  };

  const getDepartments = () => {
    const depts = new Set(teams.map(t => t.Department));
    return Array.from(depts);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Header */}
      <header
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e4e8',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '600' }}>Team Management</h1>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
              Manage teams, members, and capacity allocation
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Message */}
      {message && (
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto 1rem',
            padding: '0.75rem 1rem',
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '4px',
            fontSize: '0.9rem',
          }}
        >
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid #e0e4e8' }}>
          {(['teams', 'members', 'capacity'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: activeTab === tab ? 'white' : 'transparent',
                color: activeTab === tab ? '#667eea' : '#666',
                border: 'none',
                borderBottom: activeTab === tab ? '3px solid #667eea' : '3px solid transparent',
                cursor: 'pointer',
                fontWeight: activeTab === tab ? '600' : '500',
                fontSize: '0.95rem',
                marginBottom: '-2px',
                transition: 'all 0.2s',
              }}
            >
              {tab === 'teams' && 'Teams'}
              {tab === 'members' && 'Team Members'}
              {tab === 'capacity' && 'Team Capacity'}
            </button>
          ))}
        </div>

        {/* TEAMS TAB */}
        {activeTab === 'teams' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Teams ({teams.length})</h2>
              <button
                onClick={handleAddTeam}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                }}
              >
                + Add Team
              </button>
            </div>

            {showTeamForm && (
              <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid #e0e4e8' }}>
                <h3 style={{ margin: '0 0 1rem 0' }}>{editingTeam ? 'Edit Team' : 'Add New Team'}</h3>
                <form onSubmit={handleSubmitTeam}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Team Name *</label>
                      <input
                        type="text"
                        value={teamFormData.teamName}
                        onChange={(e) => setTeamFormData({ ...teamFormData, teamName: e.target.value })}
                        placeholder="e.g., Engineering Team"
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Department *</label>
                      <input
                        type="text"
                        value={teamFormData.department}
                        onChange={(e) => setTeamFormData({ ...teamFormData, department: e.target.value })}
                        placeholder="e.g., Engineering"
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Team Lead Email *</label>
                      <select
                        value={teamFormData.teamLeadEmail}
                        onChange={(e) => setTeamFormData({ ...teamFormData, teamLeadEmail: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                      >
                        <option value="">-- Select Team Lead --</option>
                        {users.map(user => (
                          <option key={user.UserId} value={user.Email}>
                            {user.Name} ({user.Email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                      }}
                    >
                      {editingTeam ? 'Update Team' : 'Create Team'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTeamForm(false)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading teams...</div>
            ) : teams.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', textAlign: 'center', color: '#666', border: '1px solid #e0e4e8' }}>
                No teams yet. Create your first team to get started.
              </div>
            ) : (
              <div style={{ overflow: 'x', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e4e8' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e4e8', backgroundColor: '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Team Name</th>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Department</th>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Team Lead</th>
                      <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600', color: '#333' }}>Members</th>
                      <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600', color: '#333' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map(team => (
                      <tr key={team.TeamId} style={{ borderBottom: '1px solid #e0e4e8', '&:hover': { backgroundColor: '#f9fafb' } }}>
                        <td style={{ padding: '1rem' }}>{team.TeamName}</td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{ backgroundColor: '#e9ecef', color: '#333', padding: '0.25rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem' }}>
                            {team.Department}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>{team.TeamLeadEmail}</td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>{team.memberCount || 0}</td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEditTeam(team)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTeam(team.TeamId)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TEAM MEMBERS TAB */}
        {activeTab === 'members' && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Filter by Team:</label>
              <select
                value={selectedTeamFilter}
                onChange={(e) => {
                  setSelectedTeamFilter(e.target.value);
                  // Reload members when filter changes
                  getTeamMembers(e.target.value || undefined).then(setTeamMembers);
                }}
                style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
              >
                <option value="">-- All Teams --</option>
                {teams.map(team => (
                  <option key={team.TeamId} value={team.TeamId}>
                    {team.TeamName}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Team Members ({teamMembers.length})</h2>
              <button
                onClick={handleAddMember}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                }}
              >
                + Add Member
              </button>
            </div>

            {showMemberForm && (
              <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid #e0e4e8' }}>
                <h3 style={{ margin: '0 0 1rem 0' }}>{editingMember ? 'Edit Member' : 'Add Member to Team'}</h3>
                <form onSubmit={handleSubmitMember}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Team *</label>
                      <select
                        value={memberFormData.teamId}
                        onChange={(e) => setMemberFormData({ ...memberFormData, teamId: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                      >
                        <option value="">-- Select Team --</option>
                        {teams.map(team => (
                          <option key={team.TeamId} value={team.TeamId}>
                            {team.TeamName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Person *</label>
                      <select
                        value={memberFormData.userId}
                        onChange={(e) => setMemberFormData({ ...memberFormData, userId: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                      >
                        <option value="">-- Select Person --</option>
                        {users.map(user => (
                          <option key={user.UserId} value={user.UserId}>
                            {user.Name} ({user.Email})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Role</label>
                      <input
                        type="text"
                        value={memberFormData.role}
                        onChange={(e) => setMemberFormData({ ...memberFormData, role: e.target.value })}
                        placeholder="e.g., Developer, Designer"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                      }}
                    >
                      {editingMember ? 'Update Member' : 'Add Member'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowMemberForm(false)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading members...</div>
            ) : teamMembers.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', textAlign: 'center', color: '#666', border: '1px solid #e0e4e8' }}>
                No members found. Add members to teams.
              </div>
            ) : (
              <div style={{ overflow: 'x', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e4e8' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e4e8', backgroundColor: '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Team</th>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Role</th>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: '#333' }}>Email</th>
                      <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600', color: '#333' }}>Allocation %</th>
                      <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600', color: '#333' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map(member => (
                      <tr key={member.TeamMemberId} style={{ borderBottom: '1px solid #e0e4e8' }}>
                        <td style={{ padding: '1rem' }}>{member.name || getUserName(member.UserId)}</td>
                        <td style={{ padding: '1rem' }}>{getTeamName(member.TeamId)}</td>
                        <td style={{ padding: '1rem' }}>{member.role || '—'}</td>
                        <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#666' }}>{member.email || getUserEmail(member.UserId)}</td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>{member.ProjectAllocationPercent || 0}%</td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEditMember(member)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleRemoveMember(member.TeamMemberId)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TEAM CAPACITY TAB */}
        {activeTab === 'capacity' && (
          <div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Filter by Team:</label>
                <select
                  value={selectedCapacityTeam}
                  onChange={(e) => setSelectedCapacityTeam(e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
                >
                  <option value="">-- All Teams --</option>
                  {teams.map(team => (
                    <option key={team.TeamId} value={team.TeamId}>
                      {team.TeamName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem' }}>Team Capacity Utilization</h2>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading capacity data...</div>
            ) : capacityData.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', textAlign: 'center', color: '#666', border: '1px solid #e0e4e8' }}>
                No capacity data available.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {capacityData
                  .filter(team => !selectedCapacityTeam || team.teamId === selectedCapacityTeam)
                  .map(team => (
                    <div
                      key={team.teamId}
                      style={{
                        backgroundColor: 'white',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        border: `1px solid ${team.isOverAllocated ? '#ffc107' : '#e0e4e8'}`,
                        boxShadow: team.isOverAllocated ? '0 0 0 1px #ffc107' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <div>
                          <h3 style={{ margin: '0 0 0.25rem 0' }}>{team.teamName}</h3>
                          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                            {team.department} • {team.memberCount} members
                          </p>
                        </div>
                        {team.isOverAllocated && (
                          <span style={{ backgroundColor: '#ffc107', color: '#333', padding: '0.25rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '600' }}>
                            Over-allocated
                          </span>
                        )}
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                          <span>Allocated Capacity</span>
                          <span style={{ fontWeight: '600' }}>{team.allocatedPercent.toFixed(1)}%</span>
                        </div>
                        <div
                          style={{
                            backgroundColor: '#e9ecef',
                            borderRadius: '4px',
                            height: '24px',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: team.isOverAllocated ? '#dc3545' : '#28a745',
                              height: '100%',
                              width: `${Math.min(100, team.allocatedPercent)}%`,
                              transition: 'width 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                            }}
                          >
                            {team.allocatedPercent > 10 && `${team.allocatedPercent.toFixed(1)}%`}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                        <div>
                          <span style={{ color: '#666' }}>Available</span>
                          <p style={{ margin: '0.25rem 0 0 0', fontWeight: '600', color: '#28a745' }}>
                            {team.availablePercent.toFixed(1)}%
                          </p>
                        </div>
                        <div>
                          <span style={{ color: '#666' }}>Total Capacity</span>
                          <p style={{ margin: '0.25rem 0 0 0', fontWeight: '600' }}>
                            {team.totalCapacity} hours
                          </p>
                        </div>
                      </div>

                      <button
                        style={{
                          marginTop: '1rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                        }}
                      >
                        Adjust Capacity
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
